import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaCheck, FaExclamationCircle } from 'react-icons/fa';
import { newsletterAPI } from '../utils/api';

const Container = styled.div`
  background: linear-gradient(135deg, var(--primary-600) 0%, var(--primary-700) 100%);
  padding: var(--space-2xl);
  border-radius: var(--radius-xl);
  color: white;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const Title = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--space-sm);
`;

const Description = styled.p`
  margin-bottom: var(--space-lg);
  opacity: 0.9;
`;

const Form = styled.form`
  display: flex;
  gap: var(--space-sm);
  max-width: 500px;
  margin: 0 auto;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const Input = styled.input`
  flex: 1;
  padding: var(--space-md);
  border: none;
  border-radius: var(--radius-md);
  font-size: 1rem;
  min-width: 0;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const EmailInput = styled(Input)`
  flex: 2;
  min-width: 200px;
`;

const Button = styled.button`
  padding: var(--space-md) var(--space-lg);
  background: white;
  color: var(--primary-600);
  border: none;
  border-radius: var(--radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const Message = styled(motion.div)`
  margin-top: var(--space-md);
  padding: var(--space-md);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-sm);
  
  ${props => props.$success ? `
    background: rgba(255, 255, 255, 0.2);
    color: white;
  ` : `
    background: rgba(239, 68, 68, 0.2);
    color: white;
  `}
`;

const NewsletterSubscription = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setMessage(t('newsletter.emailPlaceholder'));
      setIsSuccess(false);
      return;
    }

    setIsSubmitting(true);
    setMessage('');

    try {
      await newsletterAPI.subscribe({ email, name });
      setMessage(t('newsletter.success'));
      setIsSuccess(true);
      setEmail('');
      setName('');
      
      // Clear message after 5 seconds
      setTimeout(() => {
        setMessage('');
      }, 5000);
    } catch (error) {
      setMessage(
        error.response?.data?.message || 
        t('newsletter.error')
      );
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Title>{t('newsletter.title')}</Title>
      <Description>
        {t('newsletter.subtitle')}
      </Description>
      
      <Form onSubmit={handleSubmit}>
        <Input
          type="text"
          placeholder={t('newsletter.namePlaceholder')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <EmailInput
          type="email"
          placeholder={t('newsletter.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        <Button type="submit" disabled={isSubmitting}>
          <FaEnvelope />
          {isSubmitting ? t('newsletter.subscribing') : t('newsletter.subscribe')}
        </Button>
      </Form>

      <AnimatePresence>
        {message && (
          <Message
            $success={isSuccess}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            {isSuccess ? <FaCheck /> : <FaExclamationCircle />}
            <span>{message}</span>
          </Message>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default NewsletterSubscription;

